import {
  Button,
  Divider,
  isWeb,
  SizableText,
  useDidFinishSSR,
  YStack,
} from '@mythical/ui'
import { Video } from 'app/blocks/video'
import useContent from 'app/hooks/use-content'
import useAnalytics from 'app/lib/analytics'
import { Content } from 'app/queries/content/content'
import { format } from 'date-fns'
import { observer } from 'mobx-react-lite'
import React, { useEffect, useState } from 'react'
import { useRouter } from 'solito/router'

import { RenderBlocks } from '../blocks/RenderBlocks'
import { VideoBlock } from '../blocks/Video'
import CommentLockModal from '../comments/CommentLockModal'
import { CommentsList } from '../comments/CommentsList'
import { Meta } from '../primitive/Meta'
import Spinner from '../primitive/Spinner'
import Section from '../structure/Section'
import ContentInfo from './ContentInfo'
import ContentLockModal from './ContentLockModal'
import ContentRating, { ratingMap, ratingMapReverse } from './ContentRating'
import RelatedContent from './RelatedContent'
import { ContentVideoArea } from './ContentVideoArea'
import { useStores } from 'app/store'
import EventBatcher from 'app/lib/watch-tracker'
import { useEpisodesList } from 'app/hooks/use-episodes-list'

import { ContentEpisodesList } from './ContentEpisodesList'
interface ContentProps {
  content?: Content
  slug: string
  series?: string
  id?: string
  preview?: boolean
  token?: string
  scrollToTop?: () => void
}

export const ContentView = observer<ContentProps>(
  ({ content, series, id, slug, preview, token, scrollToTop }) => {
    const router = useRouter()
    const hasMounted = useDidFinishSSR()
    const { userStore } = useStores()
    const analytics = useAnalytics()

    const {
      data: originalData,
      loading,
      error,
      setParams,
      fetchData,
      collectionName,
      collectionId,
      rateContent,
      toggleFavourite,
      mutating,
    } = useContent({
      id,
      slug,
      preview,
      token,
    })

    const [data, setData] = useState(content || originalData)
    const [isUnlocked, setIsUnlocked] = useState(data?.isUnlocked)

    useEffect(() => {
      if (hasMounted && !originalData?.slug && loading) return

      setData(originalData || content)
      setIsUnlocked(originalData?.isUnlocked)
    }, [originalData, loading, hasMounted, data?.slug, slug])

    useEffect(() => {
      if (data?.id && userStore) {
        const batcher = EventBatcher.getInstance(userStore)

        batcher.addEvent({
          content: data?.id,
        })
      }
    }, [data?.id, userStore])

    let currentSeries = series

    if (
      !currentSeries &&
      data?.isInSeries &&
      (!collectionId || data?.primaryCollectionId === collectionId)
    ) {
      currentSeries = data?.primaryCollectionId
    }

    const handlePress = (slug: string, url: string) => {
      setParams({ slug })
      if (isWeb) {
        router.replace(url, undefined, {
          shallow: true,
          scroll: true,
        })
      }
      scrollToTop?.()
    }

    const episodesList = useEpisodesList(currentSeries, data?.id, handlePress)

    const disableRecommendations = data?.disableRecommendations

    const datePublished =
      data?.createdAt && format(new Date(data.createdAt), 'MMM d, yyyy')

    const nextSlug = currentSeries ? episodesList.nextSlug : data?.next?.slug
    const prevSlug = currentSeries
      ? episodesList.previousSlug
      : data?.previous?.slug

    const nextUrl = currentSeries
      ? `/series/${episodesList.slug}/${nextSlug}`
      : `/content/${nextSlug}`
    const prevUrl = series
      ? `/series/${episodesList.slug}/${prevSlug}`
      : `/content/${prevSlug}`

    const blocks = data?.content || []

    const videoBlock = blocks.find((b) => b.__typename === 'Video') as Video
    const nonVideoBlocks = blocks.filter((b) => b.__typename !== 'Video')
    const disableComments = data?.disableComments
    const canUnlock = data?.canUnlock && !data?.preventUnlocks && videoBlock
    const hasContent = data?.content && data.content.length > 0
    const isLocked =
      !preview && // Not being previewed by an admin
      (!data?.isUnlocked || // User does not have access to it
        (data?.preventUnlocks && !data?.isUnlocked && data?.preventPreviews)) // User cannot preview it

    const showVideo =
      videoBlock && (preview || !data?.preventPreviews || data?.isUnlocked)

    const handleUnlock = () => {
      fetchData().then(() => setIsUnlocked(true))
    }

    const longPreviewCollections = ['Roll for Mythicality']
    const hasLongPreview =
      data &&
      (data.collections.some((c) => longPreviewCollections.includes(c.label)) ||
        longPreviewCollections.includes(data.category))

    return (
      <Section padded bg="$secondary10">
        <YStack gap="$3" $gtLg={{ fd: 'row', ai: 'flex-start', gap: '$4' }}>
          <YStack flex={1} gap="$3" $gtXs={{ gap: '$5' }}>
            {loading && !data?.content && <Spinner size="large" />}
            {error && (
              <YStack space="$3">
                <SizableText
                  textTransform="uppercase"
                  fontSize="$2"
                  color="$secondary6"
                  my="$1.5"
                >
                  Something went wrong
                </SizableText>
                <Button
                  onPress={() => {
                    fetchData()
                  }}
                >
                  Try Again
                </Button>
              </YStack>
            )}
            {data && (
              <Meta
                title={data.shareTitle || data.title}
                description={data.shareDescription}
                image={data.image}
                type="content"
                slug={data.slug}
              />
            )}
            {data && isLocked && !showVideo && (
              <ContentLockModal
                onUnlock={handleUnlock}
                id={data.id}
                image={data.image}
                isVideo={data.videoPost}
                loading={loading || !hasMounted}
                planLevel={data?.contentTier}
                canUnlock={!!canUnlock}
              />
            )}
            {data && (
              <YStack gap="$3" $gtXs={{ gap: '$5' }}>
                {videoBlock && showVideo && (
                  <ContentVideoArea
                    contentId={data.id}
                    title={data.title}
                    thumbnail={data.image}
                    block={videoBlock}
                    isUnlocked={data.isUnlocked}
                    canUnlock={!!canUnlock}
                    planLevel={data?.contentTier}
                    currentProgress={data?.currentProgress}
                    onUnlock={handleUnlock}
                    previewPercentage={hasLongPreview ? 25 : 10}
                    onVideoPlay={() => {
                      analytics.logEvent('video_play', {
                        content_id: data.id,
                        content_title: data.title,
                        collection_id: data.primaryCollectionId,
                        collection_title: data.category,
                      })
                    }}
                    onVideoPause={() => {
                      analytics.logEvent('video_pause', {
                        content_id: data.id,
                        content_title: data.title,
                        collection_id: data.primaryCollectionId,
                        collection_title: data.category,
                      })
                    }}
                    onVideoEnd={() => {
                      analytics.logEvent('video_complete', {
                        content_id: data.id,
                        content_title: data.title,
                        collection_id: data.primaryCollectionId,
                        collection_title: data.category,
                      })

                      if (currentSeries && nextSlug) {
                        handlePress(nextSlug, nextUrl)
                      }
                    }}
                    onVideoProgress={(progress) => {
                      analytics.logEvent('video_progress', {
                        content_id: data.id,
                        content_title: data.title,
                        collection_id: data.primaryCollectionId,
                        collection_title: data.category,
                        progress,
                      })
                    }}
                  />
                )}

                <ContentInfo
                  key={`info-${data.id}`}
                  title={data.title}
                  slug={data.slug}
                  category={data.category}
                  categoryHref={data.categoryHref}
                  datePublished={datePublished}
                  commentCount={data.commentCount}
                  tier={data.contentTier}
                  tierSlug={data.contentTierSlug}
                  isVideo={data.videoPost}
                  tags={data.tags}
                  likeCount={data?.totalFavourites}
                  hasLiked={data?.isFavourited}
                  likeLoading={mutating}
                  onLike={toggleFavourite}
                  disableComments={disableComments}
                  series={currentSeries && episodesList.slug}
                />
                {hasContent && !isLocked && (
                  <RenderBlocks
                    blocks={nonVideoBlocks}
                    slug={data.slug}
                    spaced
                  />
                )}
              </YStack>
            )}
          </YStack>

          {currentSeries && (
            <ContentEpisodesList
              title={episodesList.title}
              episodes={episodesList.episodes}
              seasons={episodesList.seasons}
              season={episodesList.activeSeason}
              loading={episodesList.loading}
              onSeasonChange={episodesList.setActiveSeason}
              onContentClick={episodesList.handleContentClick}
              currentContentId={data?.id}
            />
          )}
        </YStack>
        {data && (
          <YStack gap="$3" $gtXs={{ gap: '$5' }}>
            <ContentRating
              displayRating={data.videoPost}
              handleRatingPress={(rating) => {
                const ratingNumber = ratingMap[rating]

                if (typeof ratingNumber === 'number') {
                  rateContent(ratingNumber)
                }
              }}
              nextHref={nextSlug ? nextUrl : undefined}
              previousHref={prevSlug ? prevUrl : undefined}
              onNextPress={(e) => {
                e.preventDefault()

                if (nextSlug) {
                  handlePress(nextSlug, nextUrl)
                }
              }}
              onPreviousPress={(e) => {
                e.preventDefault()

                if (prevSlug) {
                  handlePress(prevSlug, prevUrl)
                }
              }}
              currentRating={ratingMapReverse[`${data?.currentRating}`]}
              isLocked={isLocked}
            />
            {!disableRecommendations &&
              !currentSeries &&
              data?.related?.length > 0 && (
                <>
                  <RelatedContent
                    title={collectionName || data?.category}
                    items={data?.related || []}
                    onNavigate={() => scrollToTop?.()}
                    isVideo={data.videoPost}
                  />
                  <Divider pt="$3" />
                </>
              )}
            {disableComments ? (
              <SizableText fontFamily="$body" color="$secondary5" size="$6">
                Comments have been disabled for this post.
              </SizableText>
            ) : (
              <>
                <SizableText
                  id="comments"
                  fontFamily="$heading"
                  color="$primary1"
                  size="$6"
                  textTransform="uppercase"
                  $gtXs={{ size: '$8' }}
                >
                  {`${data.commentCount}`} Comments
                </SizableText>
                {data && isUnlocked ? (
                  <CommentsList parent={data.id} />
                ) : (
                  <CommentLockModal
                    onUnlock={handleUnlock}
                    id={data.id}
                    image={data?.image}
                    planLevel={data?.contentTier}
                  />
                )}
              </>
            )}
          </YStack>
        )}
      </Section>
    )
  }
)
